import React, { useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';

import { useApplicationProjects as useApplicationProjectsQuery, useApplicationComponents } from '../hooks/useApplication';
import { StatCard, ChartCard } from '@/components/ui/card';
import { RiskGaugeCard } from '@/components/ui/risk-gauge-card';
import { DataTable } from '../components/ui/data-table';
import {
  SEVERITY_COLORS,
  useOverallRiskDistribution,
  useVulnerabilityDistribution,
  useLicenseDistribution,

  getRiskCategoryForTitle
} from '@/components/charts/distributionUtils';
import {
  VulnerabilityDistribution,
  LicenseRiskDistribution,
  OverallRiskDistribution,
  ComponentRiskHeatMap,
  TechnicalDebtChart,
  RiskLandscapeChart
} from '@/components/charts';
import { Badge } from '@/components/ui/badge';
import { formatScore, getColorForTitle, getIconForTitle, getRiskLevelBadgeClass, getRiskScoreComments, getScoreClass, getStatusColor, toNumber } from '@/helpers/common';
import { useTheme } from 'next-themes'

interface ApplicationProps {
  applicationId: number;
  onNavigate?: (route: string, params?: any) => void;
}

// Sort Risk Level by severity rather than alphabetically (Critical > High > Medium > Low).
const RISK_LEVEL_ORDER: Record<string, number> = {
  Critical: 4,
  High: 3,
  Medium: 2,
  Low: 1,
};


export function ApplicationViewReport({ applicationId, onNavigate }: ApplicationProps) {
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const { data: projects = [], isLoading: projectsLoading, error: projectsError, refetch: fetchApplicationProjects } = useApplicationProjectsQuery(applicationId);
  const { data: components = [], isLoading: componentsLoading, error: componentsError } = useApplicationComponents(applicationId);

  const loading = projectsLoading || componentsLoading;

  const error = projectsError || componentsError;

  const statCards: Array<{ title: string;comment?:string; value: number | string; hint?: string; onClick?: () => void }> = [];
  const applicationData: Array<{
    id: number;
    name: string;
    description: string;
    projectCount: number;
    overallscore: number;
    vulnerabilityscore: number;
    //licenseissuesfound: number;
    licensescore:number;
    operationalriskscore: number;
    businessriskscore: number;
    technicaldebtscore: number;
    totalComponents: number;
  }> = [];

  let firstRowStats: Array<{ title: string; value: number | string; hint?: string; onClick?: () => void }> = [];
  let secondRowStats: Array<{ title: string;comment?:string; value: number | string; hint?: string; onClick?: () => void }> = [];


  if (projects && projects.length > 0) {
    const summary = projects.reduce((acc: any, project: any) => {
      const scanCount = toNumber(project.total_scan_count);
      const componentCount = toNumber(project.total_component_count);
      const latestComponentCount = toNumber(project.latest_component_count);
      const uniqueComponentCount = toNumber(project.unique_component_count);

      const overallScore = toNumber(project.avg_overall_score ?? project.latest_overall_score);
      const vulnerabilityScore = toNumber(project.avg_vulnerability_score ?? project.latest_vulnerability_score);
      const licenseScore = toNumber(project.avg_license_score ?? project.latest_license_score);
      const operationalScore = toNumber(project.avg_operational_risk_score ?? project.latest_operational_risk_score);
      const businessRiskScore = toNumber(project.avg_business_risk_score);
      const technicalDebtScore = toNumber(project.avg_technical_debt_score ?? project.latest_technical_debt_score);
      const encryptionComponents = toNumber(project.encryption_count);
      const provenanceComponents = toNumber(project.provenance_count);

      acc.totalProjects += 1;
      acc.totalScans += scanCount;
      acc.totalComponents += componentCount;
      acc.totalLatestComponents += latestComponentCount;
      acc.totalUniqueComponents += uniqueComponentCount;
      acc.totalTechnicalDebt += technicalDebtScore;
      acc.totalBusinessRisk += businessRiskScore;
      acc.totalOverallScore += overallScore;
      acc.totalVulnerabilityScore += vulnerabilityScore;
      acc.totalLicenseScore += licenseScore;
      acc.totalOperationalScore += operationalScore;
      acc.totalEncryptionComponents += encryptionComponents;
      acc.totalProvenanceComponents += provenanceComponents;

      return acc;
    }, {
      totalProjects: 0,
      totalScans: 0,
      totalComponents: 0,
      totalLatestComponents: 0,
      totalUniqueComponents: 0,
      totalTechnicalDebt: 0,
      totalBusinessRisk: 0,
      totalOverallScore: 0,
      totalVulnerabilityScore: 0,
      totalLicenseScore: 0,
      totalOperationalScore: 0,
      totalEncryptionComponents: 0,
      totalProvenanceComponents: 0,
    });

    statCards.push(
      {
        title: "Total Projects",
        value: summary.totalProjects,
        hint: 'Count of non-empty projects with scan activity. One project can contain multiple scans or versions.',
        onClick: () => navigate(`/projects?applicationId=${applicationId}`)
      },
      {
        title: "Total Scans",
        value: Math.round(summary.totalScans),
        hint: 'Total number of scans run across all applications and projects.',
        onClick: () => navigate(`/scans?applicationId=${applicationId}`)
      },
      {
        title: "Total Components",
        value: Math.round(summary.totalComponents),
        hint: 'Total components detected across all scans.',
        onClick: () => navigate(`/components-hub?applicationId=${applicationId}`)
      },
 {
        title: 'Total Encryption Algorithms',
        value: Math.round(summary.totalEncryptionComponents),
        hint: 'Total number of cryptographic algorithms detected across all scanned applications.'
      },
      {
        title: 'Provenance',
        value: Math.round(summary.totalProvenanceComponents),
        hint: 'Number of component contributors whose geographic location could be determined from available metadata.'
      },
      // {
      //   title: "Total Unique Components",
      //   value: Math.round(summary.totalUniqueComponents),
      //   hint: 'Number of distinct components (duplicates removed).'
      // },
       {
        title: "Overall Risk Score",
        value: (summary.totalOverallScore / summary.totalProjects).toFixed(2),
        comment:getRiskScoreComments('overall_risk_score', (summary.totalOverallScore / summary.totalProjects)),
        hint: 'Organization-level risk score combining License, Vulnerability, and Operational risk signals. Higher score = higher combined exposure.'
      },
      {
        title: "Total Technical Debt",
        value: (summary.totalTechnicalDebt / summary.totalProjects).toFixed(2),
          comment:getRiskScoreComments('total_technical_debt', (summary.totalTechnicalDebt / summary.totalProjects)),
        hint: 'Reflects the effort required to remediate identified issues. Combines overall risk with business impact data collected from surveys.'
      },
      // {
      //   title: "Business Risk",
      //   value: (summary.totalBusinessRisk / summary.totalProjects).toFixed(2),
      //   hint: 'Represents business impact derived from criticality and exposure. Overall project risk based on license, vulnerability, and operational scores.'
      // },
       {
        title: 'Operational Risk Score',
        value: (summary.totalOperationalScore / summary.totalProjects).toFixed(2),
          comment:getRiskScoreComments('operational_risk_score', (summary.totalOperationalScore / summary.totalProjects)),
        hint: 'Reflects the presence of deprecated, unmaintained, or outdated components. Considers last update dates and maintainer deprecation or archival declarations.'
      },
     
      {
        title: "Vulnerability Risk Score",
        value: (summary.totalVulnerabilityScore / summary.totalProjects).toFixed(2),
        comment:getRiskScoreComments('vulnerability_risk_score', (summary.totalVulnerabilityScore / summary.totalProjects)),  
        hint: 'Reflects the presence of Critical and High severity vulnerabilities. Factors in exploit availability, EPSS likelihood, and survey inputs.'
      },
      {
        title: "License Risk Score",
        value: (summary.totalLicenseScore / summary.totalProjects).toFixed(2),
        comment:getRiskScoreComments('license_risk_score', (summary.totalLicenseScore / summary.totalProjects)),
        hint: 'Reflects the presence of Commercial or Strong Copyleft licenses across your applications. Critical licenses carry higher weight in the calculation.'
      },
     
     
    );

    firstRowStats = statCards.slice(0, 5);
    secondRowStats = statCards.slice(5);

    const applicationMap = new Map<number, {
      id: number;
      name: string;
      description: string;
      projectCount: number;
      overallscore: number;
      vulnerabilityscore: number;
      //licenseissuesfound: number;
      licensescore:number;
      operationalriskscore: number;
      businessriskscore: number;
      technicaldebtscore: number;
      totalComponents: number;
    }>();

    projects.forEach((project: any) => {
      if (!project.applicationid) {
        return;
      }

      const appId = project.applicationid;
      const metrics = {
        overallscore: toNumber(project.avg_overall_score ?? project.latest_overall_score),
        vulnerabilityscore: toNumber(project.avg_vulnerability_score ?? project.latest_vulnerability_score),
        licensescore: toNumber(project.avg_license_score ?? project.latest_license_score ?? project.total_license_issues),
       
        operationalriskscore: toNumber(project.avg_operational_risk_score ?? project.latest_operational_risk_score),
        businessriskscore: toNumber(project.avg_business_risk_score),
        technicaldebtscore: toNumber(project.avg_technical_debt_score ?? project.latest_technical_debt_score),
        totalComponents: toNumber(project.total_component_count ?? project.latest_component_count),
      };

      if (applicationMap.has(appId)) {
        const existing = applicationMap.get(appId)!;
        const previousCount = existing.projectCount;
        const newCount = previousCount + 1;

        applicationMap.set(appId, {
          ...existing,
          projectCount: newCount,
          overallscore: ((existing.overallscore * previousCount) + metrics.overallscore) / newCount,
          vulnerabilityscore: ((existing.vulnerabilityscore * previousCount) + metrics.vulnerabilityscore) / newCount,
          licensescore: ((existing.licensescore * previousCount) + metrics.licensescore) / newCount,
          operationalriskscore: ((existing.operationalriskscore * previousCount) + metrics.operationalriskscore) / newCount,
          businessriskscore: ((existing.businessriskscore * previousCount) + metrics.businessriskscore) / newCount,
          technicaldebtscore: ((existing.technicaldebtscore * previousCount) + metrics.technicaldebtscore) / newCount,
          totalComponents: existing.totalComponents + metrics.totalComponents,
        });
      } else {
        applicationMap.set(appId, {
          id: appId,
          name: project.application_name || project.name || `Application ${appId}`,
          description: project.description || 'No description available',
          projectCount: 1,
          overallscore: metrics.overallscore,
          vulnerabilityscore: metrics.vulnerabilityscore,
          licensescore: metrics.licensescore,
          operationalriskscore: metrics.operationalriskscore,
          businessriskscore: metrics.businessriskscore,
          technicaldebtscore: metrics.technicaldebtscore,
          totalComponents: metrics.totalComponents,
        });
      }
    });

    applicationData.push(
      ...Array.from(applicationMap.values()).sort((a, b) => b.overallscore - a.overallscore)
    );
  }

  const overallRiskData = useOverallRiskDistribution(components);
  const vulnerabilityData = useVulnerabilityDistribution(components);
  const licenseData = useLicenseDistribution(components);


  const projectsChartData = React.useMemo(() => {
    return projects.map(project => ({
      id: toNumber(project.id),
      name: project.name,
      overallscore: toNumber(project.avg_overall_score ?? project.latest_overall_score),
      vulnerabilityscore: toNumber(project.avg_vulnerability_score ?? project.latest_vulnerability_score),
      licensescore: toNumber(project.avg_license_score ?? project.latest_license_score),
      licenseissuesfound: toNumber(project.total_license_issues),
      operationalriskscore: toNumber(project.avg_operational_risk_score ?? project.latest_operational_risk_score),
      technicaldebtscore: toNumber(project.avg_technical_debt_score ?? project.latest_technical_debt_score),
      businessriskscore: toNumber(project.avg_business_risk_score),
    }));
  }, [projects]);


  if (loading) {
    return (
      <div className="p-4 space-y-2 max-w-full" style={{ paddingTop: "10px" }}>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-muted-foreground">Loading application data...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 space-y-2 max-w-full" style={{ paddingTop: "10px" }}>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-red-600">{error.message}</div>
        </div>
        <div className="flex justify-center">
          <Button onClick={() => fetchApplicationProjects()}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-2 max-w-full" style={{ paddingTop: "10px" }}>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {firstRowStats.map((score) => {
          return (<StatCard
            key={score.title}
            title={score.title}
            value={score.value}
            icon={getIconForTitle(score.title)}
            color={getColorForTitle(score.title, statCards.findIndex(s => s.title === score.title))}
            hint={score.hint}
            onClick={score.onClick}
          />);
        })}
      </div>

      {secondRowStats.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mt-4">
          {secondRowStats.map((score) => {
            const numValue = typeof score.value === 'string' ? parseFloat(score.value) : score.value;
            const riskCategory = getRiskCategoryForTitle(score.title, numValue) as keyof typeof SEVERITY_COLORS;
            const riskColor = SEVERITY_COLORS[riskCategory];
            const riskLabel = riskCategory.charAt(0).toUpperCase() + riskCategory.slice(1);

            return (
              <RiskGaugeCard
                key={score.title}
                title={score.title}
                value={score.value}
                riskColor={riskColor}
                riskLabel={riskLabel}
                hint={score.hint}
                comment={score.comment}
              />
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <OverallRiskDistribution data={overallRiskData} onNavigate={onNavigate} applicationId={applicationId} />
        <VulnerabilityDistribution data={vulnerabilityData} onNavigate={onNavigate} applicationId={applicationId} />
        <LicenseRiskDistribution data={licenseData} onNavigate={onNavigate} applicationId={applicationId} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <ComponentRiskHeatMap data={projectsChartData} />
        <RiskLandscapeChart data={projectsChartData} />
      </div>

      <div className="space-y-6 mt-4">
        <DataTable
          data={projects}
          title="Project Details"
          description="Aggregated project health metrics sourced from project statistics"
          searchable={true}
          searchPlaceholder="Search projects..."
          itemsPerPage={1000}
          onRowClick={(project) => onNavigate && onNavigate('project-view-report', { projectId: project.id })}
          rowClassName={() => "cursor-pointer hover:bg-muted/50"}
          columns={[
            {
              key: 'name',
              header: 'Project Name',
              align: 'left',
              sortable: true,
              render: (value) => (
                <span className="font-medium">{value}</span>
              )
            },
            {
              key: 'risk_level',
              header: 'Risk Level',
              align: 'center',
              sortable: true,
              sortAccessor: (project) => RISK_LEVEL_ORDER[project.risk_level] ?? 0,
              render: (value) => {
                const riskLevel = value || 'Unknown';
                return (
                  <Badge className={getRiskLevelBadgeClass(riskLevel)}>
                    {riskLevel}
                  </Badge>
                );
              }
            },
            {
              key: 'total_scan_count',
              header: 'Total Scans',
              align: 'center',
              sortable: true,
              render: (value) => {
                const totalScans = Math.round(toNumber(value));
                return totalScans.toLocaleString();
              }
            },
            {
              key: 'components',
              header: 'Components (total / unique)',
              align: 'center',
              sortable: true,
              sortAccessor: (project) => toNumber(project.total_component_count),
              render: (_, project) => {
                const totalComponents = Math.round(toNumber(project.total_component_count));
                const uniqueComponents = Math.round(toNumber(project.unique_component_count));
                return `${totalComponents.toLocaleString()} / ${uniqueComponents.toLocaleString()}`;
              }
            },
            {
              key: 'overall_score',
              header: 'Overall Score',
              hint: 'Organization-level risk score combining License, Vulnerability, and Operational risk signals. Higher score = higher combined exposure.',
              align: 'center',
              sortable: true,
              sortAccessor: (project) => toNumber(project.avg_overall_score ?? project.latest_overall_score),
              render: (_, project) => {
                const overallScore = toNumber(project.avg_overall_score ?? project.latest_overall_score);
                return (
                  <span className={getScoreClass(overallScore)}>{formatScore(overallScore)}</span>
                );
              }
            },
            {
              key: 'vulnerability_score',
              header: 'Vulnerability Score',
              hint: 'Reflects the presence of Critical and High severity vulnerabilities. Factors in exploit availability, EPSS likelihood, and survey inputs.',
              align: 'center',
              sortable: true,
              sortAccessor: (project) => toNumber(project.avg_vulnerability_score ?? project.latest_vulnerability_score),
              render: (_, project) => {
                const vulnerabilityScore = toNumber(project.avg_vulnerability_score ?? project.latest_vulnerability_score);
                return (
                  <span className={getScoreClass(vulnerabilityScore)}>{formatScore(vulnerabilityScore)}</span>
                );
              }
            },
            {
              key: 'license_score',
              header: 'License Risk Score',
              hint:"Reflects the presence of Commercial or Strong Copyleft licenses across your applications. Critical licenses carry higher weight in the calculation.",
              align: 'center',
              sortable: true,
              sortAccessor: (project) => toNumber(project.avg_license_score ?? project.latest_license_score ?? project.total_license_issues),
              render: (_, project) => {
                const licenseScore = toNumber(project.avg_license_score ?? project.latest_license_score ?? project.total_license_issues);
                return (
                  <span className={getScoreClass(licenseScore)}>{formatScore(licenseScore)}</span>
                );
              }
            },
            {
              key: 'operational_score',
              header: 'Operational Risk',
              hint:"Reflects the presence of deprecated, unmaintained, or outdated components. Considers last update dates and maintainer deprecation or archival declarations.",
              align: 'center',
              sortable: true,
              sortAccessor: (project) => toNumber(project.avg_operational_risk_score ?? project.latest_operational_risk_score),
              render: (_, project) => {
                const operationalScore = toNumber(project.avg_operational_risk_score ?? project.latest_operational_risk_score);
                return (
                  <span className={getScoreClass(operationalScore)}>{formatScore(operationalScore)}</span>
                );
              }
            },
            // {
            //   key: 'business_risk',
            //   header: 'Business Risk',
            //   align: 'center',
            //   sortable: true,
            //   render: (_, project) => {
            //     const businessRisk = roundToOneDecimal(toNumber(project.avg_business_risk_score));
            //     return (
            //       <span className={getScoreClass(businessRisk)}>{formatScore(businessRisk)}</span>
            //     );
            //   }
            // },
            {
              key: 'technical_debt',
              header: 'Technical Debt',
              hint:"Reflects the effort required to remediate identified issues. Combines overall risk with business impact data collected from surveys.",
              align: 'center',
              sortable: true,
              sortAccessor: (project) => toNumber(project.avg_technical_debt_score ?? project.latest_technical_debt_score),
              render: (_, project) => {
                const technicalDebt = toNumber(project.avg_technical_debt_score ?? project.latest_technical_debt_score);
                return (
                  <span className={getScoreClass(technicalDebt)}>{formatScore(technicalDebt)}</span>
                );
              }
            },
            {
              key: 'status',
              header: 'Status',
              align: 'center',
              sortable: true,
              render: (_, project) => {
                const statusLabel = project.status || (project.isarchived ? 'Archived' : project.isactive ? 'Active' : 'Inactive');
                return (
                  <Badge className={getStatusColor(statusLabel)}>
                    {statusLabel}
                  </Badge>
                );
              }
            }
          ]}
        /></div>
    </div>
  );
}
